import { useState } from "react";
import { App } from "antd";
import type {
  AssessmentSettlementType,
  RespondDeliveryOfferResponse,
  UpdateDeliveryCommentResponse,
} from "@gull/shared";

import { ApiError, enduserApi } from "./api";

export type OfferResponse = "ACCEPTED" | "DECLINED";

type ActionResult<T = unknown> = ({ ok: true } & T) | { ok: false };

/**
 * Acting on a delivery — commenting on it, answering its offer — from either capability the backend
 * accepts: the token from the e-mailed link, or a signed-in owner. Which one is held decides both
 * the request helper and, for a response, the endpoint, so that branch lives here rather than at
 * each of the three call sites.
 *
 * Failures are reported to the customer here and returned as `{ ok: false }` so the caller can
 * decide what to do next — the status page refetches on a comment failure, because the usual cause
 * is a token that has since expired and the page needs to fall back to read-only.
 */
export function useDeliveryActions(input: { reference: string | null; token?: string | null }) {
  const { reference, token } = input;
  const { message } = App.useApp();
  const [responding, setResponding] = useState<OfferResponse | null>(null);

  const failed = (err: unknown, fallback: string): { ok: false } => {
    message.error(err instanceof ApiError ? err.message : fallback);
    return { ok: false };
  };

  const saveComment = async (
    customerComment: string | null,
  ): Promise<ActionResult<{ customerComment: string | null }>> => {
    if (!reference) return failed(null, "Mangler referanse for innleveringen");
    const path = `/deliveries/status/${encodeURIComponent(reference)}/comment`;
    const body = { customerComment };
    try {
      const result = token
        ? await enduserApi.publicPatch<UpdateDeliveryCommentResponse>(
            `${path}?token=${encodeURIComponent(token)}`,
            body,
          )
        : await enduserApi.authedPatch<UpdateDeliveryCommentResponse>(path, body);
      message.success("Kommentar lagret");
      return { ok: true, customerComment: result.customerComment };
    } catch (err) {
      return failed(err, "Kunne ikke lagre kommentar");
    }
  };

  const respond = async (
    response: OfferResponse,
    settlementType: AssessmentSettlementType,
    comment = "",
  ): Promise<ActionResult> => {
    // The token answers through the approval link it was minted for; a session answers by reference.
    if (!token && !reference) return failed(null, "Mangler referansekode for innleveringen");
    setResponding(response);
    const body = {
      response,
      settlementType: response === "ACCEPTED" ? settlementType : undefined,
      comment: comment.trim() || undefined,
    };
    try {
      // Two doors, differing only in how we prove the right to answer.
      //
      // With a token — the customer followed the link from the offer e-mail — the token names the
      // delivery and is itself the credential, so the request carries no bearer. `publicPost` is
      // required rather than preferred: an anonymous visitor has no Firebase user, and `authedPost`
      // throws before it reaches the network.
      //
      // Without one, the customer is signed in on Min side, so the bearer proves who they are and
      // the reference in the body says which delivery. The server checks they own it.
      if (token) {
        await enduserApi.publicPost<RespondDeliveryOfferResponse>(
          `/deliveries/approval/respond/${encodeURIComponent(token)}`,
          body,
        );
      } else {
        await enduserApi.authedPost<RespondDeliveryOfferResponse>("/deliveries/approval/respond", {
          ...body,
          referenceNumber: reference,
        });
      }
      message.success(response === "ACCEPTED" ? "Tilbud godkjent" : "Tilbud avslått");
      return { ok: true };
    } catch (err) {
      return failed(err, "Kunne ikke registrere svar");
    } finally {
      setResponding(null);
    }
  };

  return { responding, saveComment, respond };
}
